import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CompetenceChips } from './CompetenceChips';
import { useUpdateCollaborator, useSetCollaboratorCategories, useDeactivateCollaborator, useActivateCollaborator } from '../../hooks/queries/useCollaborators';
import { cn } from '../../lib/cn';
import type { CollaboratorWithCategories, CollaboratorRole } from '../../services/collaborators/collaboratorService';

interface EditFormValues {
  full_name: string;
  role: CollaboratorRole;
  phone: string;
  bar_number: string;
  hourly_rate: string; // string in form, parsed to number on submit
  is_active: boolean;
  category_ids: string[];
}

interface CollaboratorEditModalProps {
  collaborator: CollaboratorWithCategories | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CollaboratorEditModal({
  collaborator,
  open,
  onOpenChange,
}: CollaboratorEditModalProps) {
  const { mutateAsync: updateCollaborator, isPending: isUpdating } = useUpdateCollaborator();
  const { mutateAsync: setCategories, isPending: isSettingCategories } = useSetCollaboratorCategories();
  const { mutateAsync: deactivateCollaborator, isPending: isDeactivating } = useDeactivateCollaborator();
  const { mutateAsync: activateCollaborator, isPending: isActivating } = useActivateCollaborator();
  const isPending = isUpdating || isSettingCategories || isDeactivating || isActivating;

  const {
    register,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm<EditFormValues>({
    defaultValues: {
      full_name: '',
      role: 'lawyer',
      phone: '',
      bar_number: '',
      hourly_rate: '',
      is_active: true,
      category_ids: [],
    },
  });

  const role = watch('role');

  // Reset form when collaborator changes
  useEffect(() => {
    if (collaborator) {
      reset({
        full_name: collaborator.full_name,
        role: (collaborator.role as CollaboratorRole) || 'lawyer',
        phone: collaborator.phone ?? '',
        bar_number: collaborator.bar_number ?? '',
        hourly_rate:
          collaborator.hourly_rate !== null && collaborator.hourly_rate !== undefined
            ? String(collaborator.hourly_rate)
            : '',
        is_active: collaborator.is_active,
        category_ids: collaborator.categories.map((c) => c.id),
      });
    }
  }, [collaborator, reset]);

  async function onSubmit(values: EditFormValues) {
    if (!collaborator) return;

    const trimmedRate = values.hourly_rate.trim().replace(',', '.');
    const parsedRate = trimmedRate === '' ? null : Number(trimmedRate);
    if (parsedRate !== null && (Number.isNaN(parsedRate) || parsedRate < 0)) {
      toast.error('Taux horaire invalide');
      return;
    }

    // Step 1 — Update business fields (is_active is stripped server-side anyway)
    try {
      await Promise.all([
        updateCollaborator({
          id: collaborator.id,
          full_name: values.full_name,
          role: values.role,
          phone: values.phone || null,
          bar_number: values.bar_number || null,
          hourly_rate: parsedRate,
        }),
        setCategories({
          collaboratorId: collaborator.id,
          categoryIds: values.category_ids,
        }),
      ]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur : ${message}`);
      return;
    }

    // Step 2 — Handle is_active change via Edge Function (set-collaborator-active)
    if (values.is_active !== collaborator.is_active) {
      try {
        if (values.is_active) {
          await activateCollaborator(collaborator.id);
        } else {
          await deactivateCollaborator(collaborator.id);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erreur inconnue';
        toast.error(`Modification du statut impossible : ${message}`);
        // Other fields were saved; don't close — user must know the activation change failed.
        return;
      }
    }

    toast.success('Collaborateur mis à jour');
    onOpenChange(false);
  }

  const showBarNumber = role === 'admin' || role === 'lawyer';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50',
            'w-full max-w-lg max-h-[90vh] overflow-y-auto',
            'bg-cime-surface rounded-cime border border-neutral-200 shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-neutral-200">
            <Dialog.Title className="font-heading text-lg font-semibold text-neutral-900">
              Modifier le collaborateur
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="p-1.5 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-6 space-y-5">
            {/* Email (read-only) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-neutral-700">
                Email
              </label>
              <div className="w-full h-10 px-3 flex items-center text-sm rounded-cime border border-neutral-200 bg-neutral-50 text-neutral-500">
                {collaborator?.email}
              </div>
            </div>

            {/* Full name */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="edit-full_name" className="text-sm font-medium text-neutral-700">
                Nom complet <span className="text-red-500">*</span>
              </label>
              <Input
                id="edit-full_name"
                placeholder="Maître Jean Dupont"
                aria-invalid={!!errors.full_name}
                {...register('full_name', { required: 'Le nom est requis' })}
              />
              {errors.full_name && (
                <p className="text-xs text-red-600" role="alert">{errors.full_name.message}</p>
              )}
            </div>

            {/* Role */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="edit-role" className="text-sm font-medium text-neutral-700">
                Rôle
              </label>
              <select
                id="edit-role"
                className="w-full h-10 px-3 text-sm rounded-cime border border-neutral-300 bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-cime-primary focus:border-transparent transition-colors"
                {...register('role')}
              >
                <option value="lawyer">Avocat</option>
                <option value="secretary">Assistant(e)</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Phone */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="edit-phone" className="text-sm font-medium text-neutral-700">
                Téléphone <span className="text-neutral-400 text-xs font-normal">(optionnel)</span>
              </label>
              <Input
                id="edit-phone"
                type="tel"
                placeholder="+33 6 12 34 56 78"
                {...register('phone')}
              />
            </div>

            {/* Bar number */}
            {showBarNumber && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="edit-bar_number" className="text-sm font-medium text-neutral-700">
                  Numéro de barreau <span className="text-neutral-400 text-xs font-normal">(optionnel)</span>
                </label>
                <Input
                  id="edit-bar_number"
                  placeholder="75XXXX"
                  {...register('bar_number')}
                />
              </div>
            )}

            {/* Hourly rate (avocats/admin uniquement) */}
            {showBarNumber && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="edit-hourly_rate" className="text-sm font-medium text-neutral-700">
                  Taux horaire HT (€/h) <span className="text-neutral-400 text-xs font-normal">(optionnel)</span>
                </label>
                <Input
                  id="edit-hourly_rate"
                  type="text"
                  inputMode="decimal"
                  placeholder="250"
                  {...register('hourly_rate')}
                />
                <p className="text-xs text-neutral-400">
                  Snapshot copié dans chaque prestation à la saisie. Modifier ici ne change pas les prestations existantes.
                </p>
              </div>
            )}

            {/* Active toggle */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-neutral-700">Compte actif</span>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={field.value}
                    onClick={() => field.onChange(!field.value)}
                    className={cn(
                      'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cime-primary focus-visible:ring-offset-2',
                      field.value ? 'bg-cime-primary' : 'bg-neutral-300',
                    )}
                  >
                    <span
                      className={cn(
                        'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
                        field.value ? 'translate-x-6' : 'translate-x-1',
                      )}
                    />
                  </button>
                )}
              />
            </div>

            {/* Competences */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">
                Domaines de compétence
              </label>
              <Controller
                name="category_ids"
                control={control}
                render={({ field }) => (
                  <CompetenceChips value={field.value} onChange={field.onChange} />
                )}
              />
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-200 pt-4 flex gap-3 justify-end">
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" disabled={isPending}>
                  Annuler
                </Button>
              </Dialog.Close>
              <Button type="submit" variant="primary" isLoading={isPending}>
                Enregistrer
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
